// The LSM-tree engine. Orchestrates WAL -> memtable -> flush -> SSTables ->
// compaction. All persistence goes through the KVStore interface; all reads
// below the memtable go through serialized SSTable bytes.

import { Skiplist } from "./skiplist";
import { Wal } from "./wal";
import { KVStore } from "./store";
import { buildTable, SSTableReader } from "./sstable";
import { Emitter, EngineEvent } from "./events";
import { Trace, TraceBuilder } from "./trace";
import {
  DEFAULT_CONFIG,
  EngineConfig,
  Entry,
  Manifest,
  TableMeta,
  cmp,
} from "./types";

const SST_TABLE = "sst";
const META_TABLE = "meta";
const HIGH_KEY = "￿￿";

export interface Metrics {
  puts: number;
  deletes: number;
  gets: number;
  /** Logical bytes the user asked us to write (keys + values). */
  userBytesWritten: number;
  /** Bytes appended to the WAL. */
  walBytesWritten: number;
  /** SSTable bytes written by flushes. */
  flushBytesWritten: number;
  /** SSTable bytes written by compactions. */
  compactionBytesWritten: number;
  /** Cumulative tables whose bloom filter was consulted during gets. */
  tablesProbed: number;
  /** Probes the bloom filter rejected (no data scan happened). */
  bloomSkips: number;
  /** Probes that scanned data and found nothing (incl. bloom false positives). */
  wastedProbes: number;
  /** Serialized entries decoded during get scans. */
  entriesScanned: number;
  flushes: number;
  compactions: number;
  droppedTombstones: number;
}

function freshMetrics(): Metrics {
  return {
    puts: 0,
    deletes: 0,
    gets: 0,
    userBytesWritten: 0,
    walBytesWritten: 0,
    flushBytesWritten: 0,
    compactionBytesWritten: 0,
    tablesProbed: 0,
    bloomSkips: 0,
    wastedProbes: 0,
    entriesScanned: 0,
    flushes: 0,
    compactions: 0,
    droppedTombstones: 0,
  };
}

export class LSM {
  readonly events = new Emitter();
  readonly metrics: Metrics = freshMetrics();
  config: EngineConfig;

  private memtable = new Skiplist();
  /** Flushed-but-not-yet-durable memtables, newest first. */
  private immutables: { table: Skiplist; maxSeq: number }[] = [];
  private manifest: Manifest;
  private wal: Wal;
  private readers = new Map<number, SSTableReader>();
  /** Serializes flush/compaction so manifest updates never interleave. */
  private maintenance: Promise<void> = Promise.resolve();
  private compacting = false;
  /** Set >0 by the UI to pace compactions so their progress is watchable. */
  compactionDelayMs = 0;
  /** Ring buffer of recent operation traces (newest last). */
  readonly traces: Trace[] = [];
  /** Tracing is cheap (a few small objects per op) but can be turned off. */
  tracing = true;
  private traceLimit = 40;
  private nextTraceId = 1;

  private constructor(
    private readonly store: KVStore,
    config: Partial<EngineConfig>,
    manifest: Manifest,
  ) {
    this.config = { ...DEFAULT_CONFIG, ...config };
    this.manifest = manifest;
    this.wal = new Wal(store);
  }

  /** Open the engine: load the manifest and replay the WAL (crash recovery). */
  static async open(store: KVStore, config: Partial<EngineConfig> = {}): Promise<LSM> {
    const raw = (await store.get(META_TABLE, "manifest")) as string | undefined;
    const numLevels = config.numLevels ?? DEFAULT_CONFIG.numLevels;
    const manifest: Manifest = raw
      ? (JSON.parse(raw) as Manifest)
      : {
          levels: Array.from({ length: numLevels }, () => []),
          nextTableId: 1,
          nextSeq: 1,
        };
    while (manifest.levels.length < numLevels) manifest.levels.push([]);
    const db = new LSM(store, config, manifest);

    // Recovery: everything acknowledged but not flushed lives in the WAL.
    const records = await db.wal.replay();
    for (const r of records) {
      db.memtable.insert(r.key, r.value, r.seq);
      if (r.seq >= manifest.nextSeq) manifest.nextSeq = r.seq + 1;
    }
    let tables = 0;
    for (const lvl of manifest.levels) tables += lvl.length;
    db.events.emit({ type: "recovered", walRecords: records.length, tables });
    db.emitMemtable();
    return db;
  }

  // ------------------------------------------------------------------ writes

  async put(key: string, value: string): Promise<void> {
    await this.write(key, value);
  }

  async delete(key: string): Promise<void> {
    await this.write(key, null);
  }

  /**
   * The write path. Four steps, in this order, for both put and delete
   * (a delete is just a write whose value is a tombstone):
   *
   *   1. Assign a sequence number — the global ordering that decides which
   *      version of a key wins everywhere downstream.
   *   2. Append to the WAL and wait for the store. Nothing before this point
   *      is durable; nothing after it can be lost.
   *   3. Insert into the skiplist memtable (O(log n), in memory).
   *   4. Check the flush threshold.
   *
   * The disk cost of a write is one sequential log append. No page of any
   * SSTable is read or rewritten here — that debt is paid later, in bulk, by
   * compaction. This is the trade at the heart of the design.
   */
  private async write(key: string, value: string | null): Promise<void> {
    if (key.length === 0) throw new Error("empty key");
    const op = value === null ? "delete" : "put";
    const t = this.beginTrace(op, key);

    const seq = this.manifest.nextSeq++;
    const entry: Entry = { key, value, seq };
    t?.step(
      "seq-assign",
      "manifest",
      `assigned sequence number ${seq}` +
        (value === null ? " to a tombstone (delete writes a marker, not a removal)" : ""),
      `value ${value === null ? "∅ (tombstone)" : `${value.length} B`}`,
    );

    // Durability first: the write is acknowledged only after the WAL append.
    const rec = await this.wal.append(entry);
    this.metrics.walBytesWritten += rec.bytes;
    t?.step(
      "wal-append",
      "wal",
      `appended record [op|seq|keyLen|key|valLen|val] at log key ${rec.logKey} — durable, write is now safe to acknowledge`,
      `${rec.bytes} B sequential`,
    );

    const ins = this.memtable.insert(key, value, seq);
    t?.step(
      "memtable-insert",
      "memtable",
      ins.overwrote
        ? `skiplist overwrite — key already in the memtable, newer seq replaces it in place`
        : `skiplist insert — new node spliced in at tower height ${ins.height} of ${this.memtable.levels}`,
      `${ins.comparisons} comparisons, O(log n)`,
    );

    if (value === null) this.metrics.deletes++;
    else this.metrics.puts++;
    this.metrics.userBytesWritten += key.length + (value?.length ?? 0);
    this.events.emit({ type: "write", key, op, seq });
    this.emitMemtable();

    const full = this.memtable.bytes >= this.config.memtableBytes;
    t?.step(
      "threshold-check",
      "memtable",
      full
        ? `memtable full — freezing it and scheduling a flush to L0`
        : `memtable below threshold, no flush`,
      `${this.memtable.bytes}/${this.config.memtableBytes} B`,
    );
    this.endTrace(
      t,
      `${op} acknowledged after 1 log append + 1 in-memory insert` +
        (full ? "; flush triggered" : ""),
    );
    if (full) this.scheduleFlush();
  }

  // ------------------------------------------------------------------- reads

  /**
   * The read path — search newest to oldest, stop at the first version found.
   *
   * A key's versions can exist in many places at once, because nothing is
   * ever updated in place. Correctness comes from search order alone:
   *
   *   1. Active memtable — the newest writes.
   *   2. Immutable memtables — frozen, flush in flight.
   *   3. Each level, shallowest first. Within a level, only runs whose
   *      [minKey, maxKey] covers the key are candidates; each candidate is
   *      probed bloom → sparse index → byte scan, and the highest sequence
   *      number wins (L0 and tiered levels can hold overlapping runs).
   *
   * The first version found is by construction the newest, so the search
   * stops there. A tombstone found first means "deleted" — it shadows every
   * older version below it.
   */
  async get(key: string): Promise<string | null> {
    this.metrics.gets++;
    const t = this.beginTrace("get", key);

    const mp = this.memtable.probe(key);
    t?.step(
      "memtable-probe",
      "memtable",
      mp.entry !== undefined
        ? `HIT in the active memtable — newest possible version, search stops`
        : `miss in the active memtable`,
      `${mp.comparisons} comparisons`,
    );
    if (mp.entry !== undefined) {
      this.endTrace(t, this.resultSummary(mp.entry.value, 0));
      return mp.entry.value;
    }

    for (let i = 0; i < this.immutables.length; i++) {
      const e = this.immutables[i].table.get(key);
      t?.step(
        "immutable-probe",
        "memtable",
        e !== undefined
          ? `HIT in immutable memtable ${i} (frozen, flush in flight)`
          : `miss in immutable memtable ${i}`,
      );
      if (e !== undefined) {
        this.endTrace(t, this.resultSummary(e.value, 0));
        return e.value;
      }
    }

    let probed = 0;
    for (let li = 0; li < this.manifest.levels.length; li++) {
      const level = this.manifest.levels[li];
      const candidates = level.filter(
        (meta) => !(cmp(key, meta.minKey) < 0 || cmp(key, meta.maxKey) > 0),
      );
      if (level.length > 0) {
        t?.step(
          "level-enter",
          "sstable",
          `L${li}: ${candidates.length} of ${level.length} run(s) have a key range covering "${key}"` +
            (candidates.length === 0
              ? " — whole level skipped without reading a byte"
              : ""),
          li === 0 ? "L0 runs overlap" : "sorted, non-overlapping",
          1,
        );
      }
      let best: Entry | undefined;
      for (const meta of candidates) {
        const reader = await this.reader(meta.id);
        this.metrics.tablesProbed++;
        probed++;
        const probe = reader.get(key);
        if (!probe.bloomPassed) {
          this.metrics.bloomSkips++;
          t?.step(
            "bloom-reject",
            "sstable",
            `sst#${meta.id}: bloom filter says NO — definitively absent, data section never touched`,
            `${reader.bloomHashes} bit tests`,
            2,
          );
          continue;
        }
        this.metrics.entriesScanned += probe.entriesScanned;
        if (probe.entry === undefined) {
          this.metrics.wastedProbes++;
          t?.step(
            "bloom-false-positive",
            "sstable",
            `sst#${meta.id}: bloom said maybe, scan found nothing — a false positive, the cost the filter trades for its size`,
            `${probe.indexComparisons} index cmp + ${probe.entriesScanned} entries decoded`,
            2,
          );
          continue;
        }
        t?.step(
          "sstable-hit",
          "sstable",
          `sst#${meta.id}: bloom PASS → binary-searched sparse index to slot ${probe.sparseSlot} ("${probe.seekAnchor}") → seek byte ${probe.seekOffset} → scanned forward, FOUND seq ${probe.entry.seq}` +
            (probe.entry.value === null ? " (tombstone)" : ""),
          `${probe.indexComparisons} index cmp + ${probe.entriesScanned} entries decoded`,
          2,
        );
        if (best === undefined || probe.entry.seq > best.seq) best = probe.entry;
      }
      if (best !== undefined) {
        t?.step(
          "level-resolve",
          "result",
          `newest version at L${li} is seq ${best.seq}` +
            (best.value === null
              ? " — a tombstone, so the key reads as deleted and deeper levels are not searched"
              : " — deeper levels hold only older versions, search stops"),
          undefined,
          1,
        );
        this.endTrace(t, this.resultSummary(best.value, probed));
        return best.value;
      }
    }
    this.endTrace(t, `not found after probing ${probed} SSTable(s)`);
    return null;
  }

  private resultSummary(value: string | null, probed: number): string {
    const what = value === null ? "not found (tombstone)" : `found "${trunc(value)}"`;
    return probed === 0
      ? `${what} — served from memory, zero SSTables read`
      : `${what} after probing ${probed} SSTable(s)`;
  }

  /** Range scan over [lo, hi): k-way merge of memtable + every run. */
  async scan(lo: string, hi: string, limit = Infinity): Promise<[string, string][]> {
    interface Cursor {
      it: Iterator<Entry>;
      head: Entry | null;
    }
    const cursors: Cursor[] = [];
    const push = (it: Iterator<Entry>) => {
      const first = it.next();
      cursors.push({ it, head: first.done ? null : first.value });
    };
    push(this.memtable.range(lo, hi));
    for (const imm of this.immutables) push(imm.table.range(lo, hi));
    for (const level of this.manifest.levels) {
      for (const meta of level) {
        if (cmp(meta.maxKey, lo) < 0 || cmp(meta.minKey, hi) >= 0) continue;
        const reader = await this.reader(meta.id);
        push(reader.range(lo, hi));
      }
    }

    const out: [string, string][] = [];
    for (;;) {
      let minKey: string | null = null;
      for (const c of cursors) {
        if (c.head && (minKey === null || cmp(c.head.key, minKey) < 0)) {
          minKey = c.head.key;
        }
      }
      if (minKey === null || out.length >= limit) break;
      let best: Entry | null = null;
      for (const c of cursors) {
        while (c.head && c.head.key === minKey) {
          if (best === null || c.head.seq > best.seq) best = c.head;
          const nxt = c.it.next();
          c.head = nxt.done ? null : nxt.value;
        }
      }
      if (best && best.value !== null) out.push([best.key, best.value]);
    }
    return out;
  }

  // ------------------------------------------------------------------- flush

  private scheduleFlush(): void {
    // Move the active memtable aside synchronously so writes never block;
    // gets keep consulting it via `immutables` until the SSTable is durable.
    if (this.memtable.count === 0) return;
    const frozen = this.memtable;
    let maxSeq = 0;
    for (const e of frozen.iterate()) if (e.seq > maxSeq) maxSeq = e.seq;
    this.memtable = new Skiplist();
    this.immutables.unshift({ table: frozen, maxSeq });
    this.emitMemtable();
    this.chain(() => this.flush(frozen, maxSeq));
  }

  /** Force a flush of whatever is in the memtable (UI button / tests). */
  async flushNow(): Promise<void> {
    this.scheduleFlush();
    await this.maintenance;
  }

  /**
   * Turn a frozen memtable into an immutable SSTable in L0.
   *
   * Because the skiplist is already in key order, this is a single sequential
   * pass — no sorting is needed at flush time. That is precisely why writes
   * were buffered in a *sorted* structure in the first place.
   *
   * Ordering is load-bearing: the WAL prefix is truncated only after the
   * SSTable and the manifest are both durable. Truncating earlier would open
   * a window where a crash loses acknowledged writes.
   */
  private async flush(frozen: Skiplist, maxSeq: number): Promise<void> {
    const entries = [...frozen.iterate()];
    if (entries.length === 0) return;
    const t = this.beginTrace("flush", `${entries.length} entries`);
    this.events.emit({
      type: "flush-start",
      entryCount: entries.length,
      bytes: frozen.bytes,
    });
    t?.step(
      "drain",
      "memtable",
      `walked the frozen skiplist in key order — already sorted, so no sort is required`,
      `${entries.length} entries`,
    );

    const built = buildTable(
      entries,
      this.config.bloomBitsPerKey,
      this.config.sparseEvery,
    );
    const meta: TableMeta = {
      id: this.manifest.nextTableId++,
      level: 0,
      ...built.meta,
    };
    t?.step(
      "serialize",
      "sstable",
      `built sst#${meta.id}: data section + sparse index (every ${this.config.sparseEvery} keys) + bloom filter (${this.config.bloomBitsPerKey} bits/key) + footer`,
      `${meta.size} B, range ${trunc(meta.minKey)}…${trunc(meta.maxKey)}`,
    );

    await this.store.put(SST_TABLE, String(meta.id), built.buffer);
    this.readers.set(meta.id, new SSTableReader(built.buffer));
    // L0 is newest-first; runs there may overlap.
    this.manifest.levels[0].unshift(meta);
    await this.persistManifest();
    t?.step(
      "publish",
      "manifest",
      `stored the bytes and added sst#${meta.id} to L0 (newest first — L0 runs may overlap, since each is an independent memtable snapshot)`,
    );

    // Only now is the flushed data durable outside the WAL.
    this.immutables = this.immutables.filter((i) => i.table !== frozen);
    await this.wal.truncate(maxSeq);
    t?.step(
      "wal-truncate",
      "wal",
      `dropped WAL records up to seq ${maxSeq} — safe only now that the SSTable is durable`,
    );

    this.metrics.flushes++;
    this.metrics.flushBytesWritten += meta.size;
    this.events.emit({ type: "flush-end", table: meta });
    this.emitMemtable();
    this.endTrace(t, `memtable → sst#${meta.id} in L0 (${meta.size} B written sequentially)`);
    this.maybeCompact();
  }

  // -------------------------------------------------------------- compaction

  /** Check triggers and run at most one compaction at a time. */
  maybeCompact(): void {
    if (this.compacting) return;
    const job = this.pickCompaction();
    if (!job) return;
    this.compacting = true;
    this.chain(async () => {
      try {
        await this.compact(job.fromLevel, job.inputs, job.toLevel);
      } finally {
        this.compacting = false;
      }
      // Cascade: one compaction can push the next level over its trigger.
      this.maybeCompact();
    });
  }

  private pickCompaction():
    | { fromLevel: number; toLevel: number; inputs: TableMeta[] }
    | null {
    const lv = this.manifest.levels;
    const last = lv.length - 1;
    if (this.config.compaction === "leveled") {
      if (lv[0].length >= this.config.l0Trigger) {
        return { fromLevel: 0, toLevel: 1, inputs: [...lv[0]] };
      }
      for (let i = 1; i < last; i++) {
        const size = lv[i].reduce((s, t) => s + t.size, 0);
        if (size > this.levelTargetBytes(i)) {
          // Pick the oldest (smallest id) run to keep selection deterministic.
          const victim = [...lv[i]].sort((a, b) => a.id - b.id)[0];
          return { fromLevel: i, toLevel: i + 1, inputs: [victim] };
        }
      }
    } else {
      // Size-tiered: when a tier accumulates enough runs, merge them all
      // into a single run one tier down.
      for (let i = 0; i < last; i++) {
        if (lv[i].length >= this.config.tieredRunTrigger) {
          return { fromLevel: i, toLevel: i + 1, inputs: [...lv[i]] };
        }
      }
      // Bottom tier: merge in place once it fragments into many runs.
      if (lv[last].length >= this.config.tieredRunTrigger) {
        return { fromLevel: last, toLevel: last, inputs: [...lv[last]] };
      }
    }
    return null;
  }

  levelTargetBytes(level: number): number {
    return this.config.levelBaseBytes * Math.pow(this.config.fanout, level - 1);
  }

  private async compact(
    fromLevel: number,
    fromInputs: TableMeta[],
    toLevel: number,
  ): Promise<void> {
    const lv = this.manifest.levels;
    // Everything in the target level overlapping the input key range joins
    // the merge (in leveled mode this preserves non-overlap; in tiered mode
    // whole tiers merge, and for the in-place bottom case the sets coincide).
    let lo = fromInputs[0].minKey;
    let hi = fromInputs[0].maxKey;
    for (const t of fromInputs) {
      if (cmp(t.minKey, lo) < 0) lo = t.minKey;
      if (cmp(t.maxKey, hi) > 0) hi = t.maxKey;
    }
    // Leveled keeps each target level non-overlapping, so every run in the
    // target whose range intersects the merged range must be rewritten too —
    // that rewriting is exactly what leveled pays for in write amplification.
    //
    // Size-tiered does NOT do this: a tier is allowed to hold several
    // overlapping runs, and the merged run simply joins them. That is what
    // makes tiered writes cheaper and tiered reads more expensive. Merging
    // with the target's runs here would quietly turn tiered into leveled.
    const overlapping =
      this.config.compaction === "leveled" && toLevel !== fromLevel
        ? lv[toLevel].filter(
            (t) => !(cmp(t.maxKey, lo) < 0 || cmp(t.minKey, hi) > 0),
          )
        : [];
    const inputs = [...fromInputs, ...overlapping];
    const inputIds = inputs.map((t) => t.id);
    const tr = this.beginTrace("compaction", `L${fromLevel}→L${toLevel}`);

    this.events.emit({
      type: "compaction-start",
      strategy: this.config.compaction,
      fromLevel,
      toLevel,
      inputTables: inputIds,
    });
    tr?.step(
      "select",
      "merge",
      `${this.config.compaction} policy picked ${fromInputs.length} run(s) from L${fromLevel} spanning ${trunc(lo)}…${trunc(hi)}`,
      `#${fromInputs.map((x) => x.id).join(", #")}`,
    );
    tr?.step(
      "overlap",
      "merge",
      overlapping.length > 0
        ? `pulled in ${overlapping.length} overlapping run(s) from L${toLevel} — they must be rewritten so the target level stays non-overlapping`
        : `no runs in L${toLevel} overlap that range, so none are rewritten`,
      overlapping.length ? `#${overlapping.map((x) => x.id).join(", #")}` : undefined,
    );

    // Real k-way merge over the serialized tables, newest-wins per key.
    interface Cursor {
      it: Iterator<Entry>;
      head: Entry | null;
    }
    const cursors: Cursor[] = [];
    let totalEntries = 0;
    for (const meta of inputs) {
      totalEntries += meta.entryCount;
      const reader = await this.reader(meta.id);
      const it = reader.iterate();
      const first = it.next();
      cursors.push({ it, head: first.done ? null : first.value });
    }

    // Tombstone retirement. Reaching the bottom level is necessary but NOT
    // sufficient: what actually matters is that no run left behind could
    // still hold an older live version of the key. Under leveled compaction
    // every overlapping run in the target is an input, so any run that
    // survives provably cannot contain these keys. Under size-tiered a tier
    // keeps overlapping runs that are not part of this merge, so a surviving
    // bottom run may well hold an older value — dropping the tombstone there
    // resurrects deleted data.
    //
    // So: only drop a tombstone if no non-input run at the bottom level has
    // a key range covering that key.
    const bottomLevel = lv.length - 1;
    const writesBottom = toLevel === bottomLevel;
    const inputIdSet = new Set(inputIds);
    const survivingBottomRuns = writesBottom
      ? lv[bottomLevel].filter((t) => !inputIdSet.has(t.id))
      : [];
    const canDrop = (key: string): boolean =>
      writesBottom &&
      !survivingBottomRuns.some(
        (t) => cmp(key, t.minKey) >= 0 && cmp(key, t.maxKey) <= 0,
      );
    const targetTableBytes = Math.max(
      4096,
      this.config.compaction === "leveled" ? this.config.memtableBytes * 2 : Infinity,
    );

    const outputs: TableMeta[] = [];
    let batch: Entry[] = [];
    let batchBytes = 0;
    let merged = 0;
    let dropped = 0;
    let bytesWritten = 0;

    const emitBatch = async () => {
      if (batch.length === 0) return;
      const built = buildTable(
        batch,
        this.config.bloomBitsPerKey,
        this.config.sparseEvery,
      );
      const meta: TableMeta = {
        id: this.manifest.nextTableId++,
        level: toLevel,
        ...built.meta,
      };
      await this.store.put(SST_TABLE, String(meta.id), built.buffer);
      this.readers.set(meta.id, new SSTableReader(built.buffer));
      outputs.push(meta);
      bytesWritten += meta.size;
      batch = [];
      batchBytes = 0;
      if (this.compactionDelayMs > 0) await sleep(this.compactionDelayMs);
    };

    for (;;) {
      let minKey: string | null = null;
      for (const c of cursors) {
        if (c.head && (minKey === null || cmp(c.head.key, minKey) < 0)) {
          minKey = c.head.key;
        }
      }
      if (minKey === null) break;
      let best: Entry | null = null;
      for (const c of cursors) {
        while (c.head && c.head.key === minKey) {
          merged++;
          if (best === null || c.head.seq > best.seq) best = c.head;
          const nxt = c.it.next();
          c.head = nxt.done ? null : nxt.value;
        }
      }
      if (best) {
        if (best.value === null && canDrop(best.key)) {
          dropped++; // nothing left below that this tombstone must shadow
        } else {
          batch.push(best);
          batchBytes += best.key.length + (best.value?.length ?? 0) + 15;
          if (batchBytes >= targetTableBytes) await emitBatch();
        }
      }
      if (merged % 64 === 0) {
        this.events.emit({
          type: "compaction-progress",
          done: merged,
          total: totalEntries,
        });
        if (this.compactionDelayMs > 0) await sleep(this.compactionDelayMs / 4);
      }
    }
    await emitBatch();

    // Swap inputs for outputs in the manifest, then persist it.
    const inputSet = new Set(inputIds);
    lv[fromLevel] = lv[fromLevel].filter((t) => !inputSet.has(t.id));
    lv[toLevel] = lv[toLevel].filter((t) => !inputSet.has(t.id));
    if (this.config.compaction === "leveled") {
      lv[toLevel] = [...lv[toLevel], ...outputs].sort((a, b) =>
        cmp(a.minKey, b.minKey),
      );
    } else {
      // Tiers are recency-ordered: the merged run is newer than any run
      // already sitting in the target tier.
      lv[toLevel] = [...outputs, ...lv[toLevel]];
    }
    await this.persistManifest();
    for (const id of inputIds) {
      await this.store.delete(SST_TABLE, String(id));
      this.readers.delete(id);
    }

    tr?.step(
      "merge",
      "merge",
      `k-way merged ${inputs.length} sorted run(s) in one pass, keeping the highest seq per key` +
        (writesBottom
          ? survivingBottomRuns.length === 0
            ? ` — this rewrites the whole bottom level, so tombstones could be discarded (nothing older survives to be resurrected)`
            : ` — bottom level, but ${survivingBottomRuns.length} run(s) here were not part of the merge, so tombstones covering their key ranges were kept`
          : ` — tombstones kept: a deeper level may still hold an older live version they must shadow`),
      `${merged} entries in → ${outputs.length} run(s) out, ${dropped} tombstones dropped`,
    );
    tr?.step(
      "install",
      "manifest",
      `atomically swapped ${inputIds.length} input run(s) for ${outputs.length} output run(s) in L${toLevel}, then deleted the inputs`,
      `${bytesWritten} B rewritten (this is write amplification)`,
    );

    this.metrics.compactions++;
    this.metrics.compactionBytesWritten += bytesWritten;
    this.metrics.droppedTombstones += dropped;
    this.endTrace(
      tr,
      `L${fromLevel}→L${toLevel}: ${inputIds.length} runs → ${outputs.length}, ${bytesWritten} B rewritten`,
    );
    this.events.emit({
      type: "compaction-end",
      fromLevel,
      toLevel,
      inputTables: inputIds,
      outputTables: outputs,
      droppedTombstones: dropped,
      bytesWritten,
    });
  }

  // ------------------------------------------------------------------- misc

  /**
   * Simulate a crash: throw away all volatile state (memtable, immutables
   * that haven't finished flushing) and reopen from the store. Anything
   * acknowledged must survive via WAL replay.
   */
  async crashAndRecover(): Promise<LSM> {
    await this.maintenance; // let in-flight IO settle; the memtable still dies
    return LSM.open(this.store, this.config);
  }

  /** Live logical bytes, measured honestly via a full scan of the read path. */
  async measureLiveBytes(): Promise<{ bytes: number; keys: number }> {
    const rows = await this.scan("", HIGH_KEY);
    let bytes = 0;
    for (const [k, v] of rows) bytes += k.length + v.length;
    return { bytes, keys: rows.length };
  }

  totalTableBytes(): number {
    let n = 0;
    for (const lvl of this.manifest.levels) {
      for (const t of lvl) n += t.size;
    }
    return n;
  }

  snapshot(): {
    memtable: { bytes: number; count: number; limit: number };
    immutables: number;
    levels: TableMeta[][];
  } {
    return {
      memtable: {
        bytes: this.memtable.bytes,
        count: this.memtable.count,
        limit: this.config.memtableBytes,
      },
      immutables: this.immutables.length,
      levels: this.manifest.levels.map((l) => [...l]),
    };
  }

  async tableReader(id: number): Promise<SSTableReader> {
    return this.reader(id);
  }

  /**
   * The active memtable's entries, in key order. Teaching UIs show the
   * individual buffered keys; the sandbox only needs the size counters in
   * snapshot().
   */
  *memtableSnapshot(): IterableIterator<Entry> {
    yield* this.memtable.iterate();
  }

  async walRecordCount(): Promise<number> {
    return this.wal.recordCount();
  }

  /** Wait until all queued flushes/compactions have finished. */
  async quiesce(): Promise<void> {
    // Each pass may schedule more work (cascading compactions), so loop.
    for (let i = 0; i < 100; i++) {
      await this.maintenance;
      if (this.memtable.bytes < this.config.memtableBytes && !this.compacting) {
        this.maybeCompact();
        await this.maintenance;
        if (!this.compacting) return;
      }
    }
  }

  setConfig(patch: Partial<EngineConfig>): void {
    this.config = { ...this.config, ...patch };
    while (this.manifest.levels.length < this.config.numLevels) {
      this.manifest.levels.push([]);
    }
    this.emitMemtable();
    if (this.memtable.bytes >= this.config.memtableBytes) this.scheduleFlush();
    this.maybeCompact();
  }

  /** Wipe everything — store, manifest, memtable. A fresh database. */
  async reset(): Promise<void> {
    await this.maintenance;
    await this.store.clear(SST_TABLE);
    await this.store.clear(META_TABLE);
    await this.store.clear("wal");
    this.readers.clear();
    this.memtable = new Skiplist();
    this.immutables = [];
    this.manifest = {
      levels: Array.from({ length: this.config.numLevels }, () => []),
      nextTableId: 1,
      nextSeq: 1,
    };
    const m = this.metrics as unknown as Record<string, number>;
    for (const k of Object.keys(m)) m[k] = 0;
    this.events.emit({ type: "manifest" });
    this.emitMemtable();
  }

  private async reader(id: number): Promise<SSTableReader> {
    let r = this.readers.get(id);
    if (!r) {
      const buf = (await this.store.get(SST_TABLE, String(id))) as
        | ArrayBuffer
        | undefined;
      if (!buf) throw new Error(`SSTable ${id} missing from store`);
      r = new SSTableReader(buf);
      this.readers.set(id, r);
    }
    return r;
  }

  private async persistManifest(): Promise<void> {
    await this.store.put(META_TABLE, "manifest", JSON.stringify(this.manifest));
    this.events.emit({ type: "manifest" });
  }

  private chain(job: () => Promise<void>): void {
    this.maintenance = this.maintenance.then(job).catch((err) => {
      // Surface maintenance failures instead of silently wedging the chain.
      console.error("maintenance job failed:", err);
    });
  }

  private beginTrace(op: string, arg: string): TraceBuilder | null {
    if (!this.tracing) return null;
    return new TraceBuilder(this.nextTraceId++, op, arg);
  }

  private endTrace(t: TraceBuilder | null, summary: string): void {
    if (!t) return;
    const trace = t.end(summary);
    this.traces.push(trace);
    if (this.traces.length > this.traceLimit) this.traces.shift();
    this.events.emit({ type: "trace", trace });
  }

  private emitMemtable(): void {
    this.events.emit({
      type: "memtable",
      bytes: this.memtable.bytes,
      count: this.memtable.count,
      limit: this.config.memtableBytes,
    } as EngineEvent);
  }
}

function sleep(ms: number): Promise<void> {
  return new Promise((res) => setTimeout(res, ms));
}

function trunc(s: string, n = 16): string {
  return s.length > n ? s.slice(0, n) + "…" : s;
}
